import { useEffect, useRef, useState } from 'react';
import { Stack, Text, Button, Alert, Paper, Center, FileButton } from '@mantine/core';
import { IconAlertCircle, IconCamera, IconX, IconUpload } from '@tabler/icons-react';
import { Html5Qrcode } from 'html5-qrcode';

interface QRScannerProps {
  onScan: (data: string) => void;
  onClose?: () => void;
}

export function QRScanner({ onScan, onClose }: QRScannerProps) {
  const [error, setError] = useState<string>('');
  const [isScanning, setIsScanning] = useState(false);
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const isInitializedRef = useRef(false);

  useEffect(() => {
    if (isInitializedRef.current) return;
    isInitializedRef.current = true;

    startScanning();

    return () => {
      stopScanning();
    };
  }, []);

  const startScanning = async () => {
    try {
      setError('');
      setIsScanning(true);

      // Check if running in secure context (HTTPS or localhost)
      const isSecureContext = window.isSecureContext || window.location.protocol === 'https:' ||
                              window.location.hostname === 'localhost' ||
                              window.location.hostname === '127.0.0.1';

      if (!isSecureContext) {
        setError(
          'Camera access requires HTTPS or localhost. ' +
          'To use the camera, access the app via http://localhost:3002/ on this device, ' +
          'or set up HTTPS for remote access.'
        );
        setIsScanning(false);
        return;
      }

      const scanner = new Html5Qrcode('qr-reader');
      scannerRef.current = scanner;

      await scanner.start(
        { facingMode: 'environment' }, // Use back camera
        {
          fps: 10, // Frame per second for QR code scanning
          qrbox: { width: 250, height: 250 }, // Scanning box size
        },
        (decodedText) => {
          // Success callback
          onScan(decodedText);
          stopScanning();
        },
        (errorMessage) => {
          // Error callback (happens frequently, we can ignore)
        }
      );
    } catch (err: any) {
      const errorMsg = err.message || 'Failed to start camera';

      // Provide more helpful error messages
      if (errorMsg.includes('Permission') || errorMsg.includes('NotAllowedError')) {
        setError('Camera permission denied. Please allow camera access in your browser settings and refresh the page.');
      } else if (errorMsg.includes('NotFoundError') || errorMsg.includes('NotReadableError')) {
        setError('No camera found or camera is already in use by another application.');
      } else {
        setError(errorMsg);
      }

      setIsScanning(false);
    }
  };

  const stopScanning = async () => {
    if (scannerRef.current && scannerRef.current.isScanning) {
      try {
        await scannerRef.current.stop();
        scannerRef.current.clear();
      } catch (err) {
        console.error('Error stopping scanner:', err);
      }
    }
    setIsScanning(false);
  };

  const handleFileUpload = async (file: File | null) => {
    if (!file) return;

    try {
      setError('');
      const scanner = new Html5Qrcode('qr-reader-upload');
      const decodedText = await scanner.scanFile(file, true);
      onScan(decodedText);
    } catch (err: any) {
      const errorMsg = err.message || 'Failed to scan QR code from image';
      setError(errorMsg.includes('No QR code found')
        ? 'No QR code found in the image. Please upload a clear image with a visible QR code.'
        : errorMsg
      );
    }
  };

  const handleClose = () => {
    stopScanning();
    onClose?.();
  };

  return (
    <Stack gap="md">
      {error && (
        <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red" withCloseButton onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper p="md" withBorder>
        <Stack gap="md">
          <Text size="sm" fw={500} ta="center">
            Position the QR code within the frame
          </Text>

          <div id="qr-reader" style={{ width: '100%' }} />

          {!isScanning && !error && (
            <Center>
              <Text size="sm" c="dimmed">
                Initializing camera...
              </Text>
            </Center>
          )}
        </Stack>
      </Paper>

      <Stack gap="sm">
        <Text size="xs" c="dimmed" ta="center">
          Make sure the QR code is well-lit and centered in the frame
        </Text>

        {onClose && (
          <Button
            variant="light"
            color="red"
            leftSection={<IconX size={16} />}
            onClick={handleClose}
            fullWidth
          >
            Cancel Scanning
          </Button>
        )}

        {!isScanning && error && (
          <>
            <Button
              variant="light"
              leftSection={<IconCamera size={16} />}
              onClick={startScanning}
              fullWidth
            >
              Retry Camera
            </Button>

            <FileButton onChange={handleFileUpload} accept="image/*">
              {(props) => (
                <Button
                  {...props}
                  variant="filled"
                  leftSection={<IconUpload size={16} />}
                  fullWidth
                >
                  Upload QR Code Photo
                </Button>
              )}
            </FileButton>

            <div id="qr-reader-upload" style={{ display: 'none' }} />
          </>
        )}
      </Stack>
    </Stack>
  );
}
